import React from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import useAxios from 'axios-hooks'
import _ from 'lodash'
import { toast } from 'react-toastify'

import BreadcrumbHeader from '~/components/BreadcrumbHeader'
import CustomTable from '~/components/CustomTable'
import { ChevronDown, ChevronRight } from 'react-feather'
import { GMB_LANGUAGE_CODE } from '~/constants/gmbLanguageCode'
import { GMB_REGION_CODE } from '~/constants/gmbRegionCode'
import { GMB_CURRENCY_CODE } from '~/constants/gmbCurrencyCode'
import api from '~/services/api'
import { ReactComponent as ClipboardIcon } from '../../../assets/img/svg/clipboard-check.svg'
import { useSelector } from 'react-redux'
import { Show } from '~/components/commons'
import { StyledSwitch } from '../LocationsList/styles'
import { FormText } from 'reactstrap'
import { CLIENT } from '~/constants/roles'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import { NumberParam, StringParam, withDefault } from 'use-query-params'

const ONE_BILLION = 1000000000

const WEEKDAY_TO_LABEL = {
  MONDAY: 'Mon: ',
  TUESDAY: 'Tue: ',
  WEDNESDAY: 'Wed: ',
  THURSDAY: 'Thu: ',
  FRIDAY: 'Fri: ',
  SATURDAY: 'Sat: ',
  SUNDAY: 'Sun: ',
}

const STATUS_TO_LABEL = {
  OPEN: 'Open',
  CLOSED_PERMANENTLY: 'Closed Permanently',
  CLOSED_TEMPORARILY: 'Closed Temporarily',
}

const MASK_TO_LABEL = {
  storeCode: 'Location Code',
  title: 'Name',
  phoneNumbers: 'Phone Numbers',
  'phoneNumbers.primaryPhone': 'Primary Phone',
  'phoneNumbers.additionalPhones': 'Additional Phones',
  categories: 'Categories',
  'categories.primaryCategory': 'Primary Category',
  'categories.additionalCategories': 'Additional Categories',
  storefrontAddress: 'Address',
  'storefrontAddress.regionCode': 'Country',
  'storefrontAddress.languageCode': 'Language',
  'storefrontAddress.postalCode': 'Postal Code',
  'storefrontAddress.administrativeArea': 'State',
  'storefrontAddress.locality': 'City',
  'storefrontAddress.sublocality': 'Local Area',
  'storefrontAddress.addressLines': 'Address Lines',
  'storefrontAddress.recipients': 'Recipients',
  'storefrontAddress.organization': 'Organization',
  websiteUri: 'Website',
  regularHours: 'Regular Hours',
  specialHours: 'Special Hours',
  adWordsLocationExtensions: 'Google Ads Phone',
  'adWordsLocationExtensions.adPhone': 'Google Ads Phone',
  openInfo: 'Open Info',
  'openInfo.openingDate': 'Opening Date',
  'openInfo.openingDate.year': 'Opening Date Year',
  'openInfo.openingDate.month': 'Opening Date Month',
  'openInfo.openingDate.day': 'Opening Date Day',
  'openInfo.status': 'Status',
  serviceItems: 'Services',
}

function gmbGetFormattedTime(time, locale) {
  if (!time) return ''

  const hours = String(time.hours).padStart(2, '0')
  const minutes = String(time?.minutes || 0).padStart(2, '0')
  const usHours = hours > 12 ? hours - 12 : hours
  const usPeriod = hours >= 12 ? 'pm' : 'am'

  return locale === 'en'
    ? `${usHours}:${minutes}${usPeriod}`
    : `${hours}:${minutes}`
}

function gmbGetFormattedDate(date, locale) {
  if (!date) return ''

  const hasDay = date?.day
  const day = String(date?.day || 1).padStart(2, '0')
  const month = String(date.month).padStart(2, '0')
  const year = date.year

  if (hasDay) {
    return locale === 'en'
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`
  } else {
    return `${month}/${year}`
  }
}

function gmbPriceToText(price, locale) {
  const currency = GMB_CURRENCY_CODE[price.currencyCode]
  const amount = Number(price.units || 0)
  const nanos = (price.nanos || 0) / ONE_BILLION

  return `${currency} ${(amount + nanos)
    .toFixed(2)
    .replace(/\./, locale === 'en' ? '.' : ',')}`
}

function renderWeekDayHours(period, index, weekday, lastDayArr, locale) {
  const lastDayPeriod = lastDayArr.find(({ closeDay }) => closeDay === weekday)
  const lastDayTime = gmbGetFormattedTime(lastDayPeriod?.closeTime, locale)
  const openingTime = gmbGetFormattedTime(period.openTime, locale)
  const closingTime = gmbGetFormattedTime(period.closeTime, locale)
  const isFirst = index === 0
  const finishesTomorrow = period.closeDay !== weekday
  return (
    <div key={index}>
      {isFirst ? <FormattedMessage id={WEEKDAY_TO_LABEL[weekday]} /> : ' | '}
      {lastDayPeriod && ` - ${lastDayTime} | `}
      {`${openingTime} - `}
      {!finishesTomorrow && `${closingTime} `}
    </div>
  )
}

function renderDiffObject(diffObject, diffMask, locale) {
  if (_.isEmpty(diffObject))
    return (
      <div>
        <FormattedMessage id="Not informed" />
      </div>
    )

  switch (diffMask) {
    case 'storeCode':
      return <div>{diffObject.storeCode}</div>
    case 'title':
      return <div>{diffObject.title}</div>
    case 'phoneNumbers':
      return (
        <div>
          <div>
            <FormattedMessage id="Primary:" />{' '}
            {diffObject.phoneNumbers.primaryPhone}
          </div>
          {diffObject.phoneNumbers?.additionalPhones?.length && (
            <div>
              <FormattedMessage id="Additional:" />{' '}
              {diffObject.phoneNumbers.additionalPhones.join(', ')}
            </div>
          )}
        </div>
      )
    case 'phoneNumbers.primaryPhone':
      return <div>{diffObject.phoneNumbers.primaryPhones}</div>
    case 'phoneNumbers.additionalPhones':
      return <div>{diffObject.phoneNumbers.additionalPhones.join(', ')}</div>
    case 'categories':
      return (
        <div>
          <div>
            <FormattedMessage id="Primary:" />{' '}
            {diffObject.categories.primaryCategory.displayName}
          </div>
          {diffObject.categories?.additionalCategories?.length && (
            <div>
              <FormattedMessage id="Additional:" />{' '}
              {diffObject.categories?.additionalCategories
                .map(item => item.displayName)
                .join(', ')}
            </div>
          )}
        </div>
      )
    case 'storefrontAddress':
      return (
        <div>
          <div>
            {diffObject.storefrontAddress.organization}
            {diffObject.storefrontAddress.organization &&
              diffObject.storefrontAddress.languageCode &&
              ' - '}
            {diffObject.storefrontAddress.languageCode && (
              <FormattedMessage
                id={
                  GMB_LANGUAGE_CODE[
                    diffObject.storefrontAddress.languageCode
                  ] ?? diffObject.storefrontAddress.languageCode
                }
              />
            )}
          </div>
          <div>
            {diffObject.storefrontAddress.regionCode && (
              <FormattedMessage
                id={
                  GMB_REGION_CODE[diffObject.storefrontAddress.regionCode] ??
                  diffObject.storefrontAddress.regionCode
                }
              />
            )}
            {diffObject.storefrontAddress.regionCode &&
              diffObject.storefrontAddress.administrativeArea &&
              ', '}
            {diffObject.storefrontAddress.administrativeArea}
            {(diffObject.storefrontAddress.administrativeArea ||
              diffObject.storefrontAddress.regionCode) &&
              diffObject.storefrontAddress.locality &&
              ', '}
            {diffObject.storefrontAddress.locality}
          </div>
          <div>
            {diffObject.storefrontAddress.addressLines[0]}
            {diffObject.storefrontAddress.addressLines[0] &&
              diffObject.storefrontAddress.sublocality &&
              ', '}
            {diffObject.storefrontAddress.sublocality}
            {(diffObject.storefrontAddress.sublocality ||
              diffObject.storefrontAddress.addressLines[0]) &&
              diffObject.storefrontAddress.postalCode &&
              ' / '}
            {diffObject.storefrontAddress.postalCode}
          </div>
          <div>
            {(diffObject.storefrontAddress.addressLines.slice(1) || []).join(
              ', '
            )}
            {(diffObject.storefrontAddress.recipients || []).join(', ')}
          </div>
        </div>
      )
    case 'storefrontAddress.regionCode':
      return (
        <div>
          <FormattedMessage
            id={
              GMB_REGION_CODE[diffObject.storefrontAddress.regionCode] ??
              diffObject.storefrontAddress.regionCode
            }
          />
        </div>
      )
    case 'storefrontAddress.languageCode':
      return (
        <div>
          <FormattedMessage
            id={
              GMB_LANGUAGE_CODE[diffObject.storefrontAddress.languageCode] ??
              diffObject.storefrontAddress.languageCode
            }
          />
        </div>
      )
    case 'storefrontAddress.postalCode':
      return <div>{diffObject.storefrontAddress.postalCode}</div>
    case 'storefrontAddress.administrativeArea':
      return <div>{diffObject.storefrontAddress.administrativeArea}</div>
    case 'storefrontAddress.locality':
      return <div>{diffObject.storefrontAddress.locality}</div>
    case 'storefrontAddress.sublocality':
      return <div>{diffObject.storefrontAddress.sublocality}</div>
    case 'storefrontAddress.addressLines':
      return <div>{diffObject.storefrontAddress.addressLines.join(', ')}</div>
    case 'storefrontAddress.recipients':
      return <div>{diffObject.storefrontAddress.recipients.join(', ')}</div>
    case 'storefrontAddress.organization':
      return <div>{diffObject.storefrontAddress.organization}</div>
    case 'websiteUri':
      return <a href={diffObject.websiteUri}>{diffObject.websiteUri}</a>
    case 'regularHours':
      const sortedRegularHours = _.sortBy(diffObject.regularHours.periods, [
        'openTime.hours',
        'openTime.minutes',
      ])
      const regularHours = _.groupBy(sortedRegularHours, 'openDay')

      return (
        <>
          <RegularHoursDiv>
            <div>
              {(regularHours.MONDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'MONDAY',
                  regularHours.SUNDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.TUESDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'TUESDAY',
                  regularHours.MONDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.WEDNESDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'WEDNESDAY',
                  regularHours.TUESDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.THURSDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'THURSDAY',
                  regularHours.WEDNESDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.FRIDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'FRIDAY',
                  regularHours.THURSDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.SATURDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'SATURDAY',
                  regularHours.FRIDAY || [],
                  locale
                )
              )}
            </div>
            <div>
              {(regularHours.SUNDAY || []).map((period, index) =>
                renderWeekDayHours(
                  period,
                  index,
                  'SUNDAY',
                  regularHours.SATURDAY || [],
                  locale
                )
              )}
            </div>
          </RegularHoursDiv>
        </>
      )
    case 'specialHours':
      const specialHours = _.sortBy(
        diffObject.specialHours.specialHourPeriods,
        ['startDate.year', 'startDate.month', 'startDate.day']
      )
      return (
        <div>
          {specialHours.map((item, index) => {
            const startDate = gmbGetFormattedDate(item?.startDate, locale)
            const startTime = gmbGetFormattedTime(item?.openTime, locale)
            const endDate = gmbGetFormattedDate(item?.endDate, locale)
            const endTime = gmbGetFormattedTime(item?.closeTime, locale)
            return (
              <div key={index}>
                <FormattedMessage id={item.closed ? 'Closed on' : 'Open on'} />
                {item.closed ? (
                  <> {startDate}</>
                ) : (
                  <>
                    {' '}
                    {startDate} <FormattedMessage id="at" /> {startTime}{' '}
                    <FormattedMessage id="to" /> {endDate}{' '}
                    <FormattedMessage id="at" /> {endTime}
                  </>
                )}
              </div>
            )
          })}
        </div>
      )
    case 'adWordsLocationExtensions':
    case 'adWordsLocationExtensions.adPhone':
      return <div>{diffObject.adWordsLocationExtensions.adPhone}</div>
    case 'openInfo':
      const openingDate = gmbGetFormattedDate(
        diffObject.openInfo?.openingDate,
        locale
      )
      return (
        <>
          <div>
            <FormattedMessage
              id={STATUS_TO_LABEL[diffObject.openInfo.status]}
            />
          </div>
          <div>{openingDate}</div>
        </>
      )
    case 'openInfo.openingDate':
    case 'openInfo.openingDate.year':
    case 'openInfo.openingDate.month':
    case 'openInfo.openingDate.day':
      const openInfoDate = gmbGetFormattedDate(
        diffObject.openInfo?.openingDate,
        locale
      )

      return <div>{openInfoDate}</div>
    case 'openInfo.status':
      return (
        <div>
          <FormattedMessage id={STATUS_TO_LABEL[diffObject.openInfo.status]} />
        </div>
      )
    case 'serviceItems':
      return (
        <div>
          {diffObject.serviceItems.map((item, index) => {
            const price = item?.price ? gmbPriceToText(item.price, locale) : ''
            const serviceName =
              item?.structuredServiceItem?.description ||
              item?.structuredServiceItem?.serviceTypeId ||
              item?.freeFormServiceItem?.label?.displayName ||
              item?.freeFormServiceItem?.label?.description ||
              item?.freeFormServiceItem?.label?.category
            return (
              <div key={index}>
                {serviceName}
                {price && (
                  <>
                    {' '}
                    <FormattedMessage id="for" /> {price}
                  </>
                )}
              </div>
            )
          })}
        </div>
      )
    default:
      return (
        <div>
          <FormattedMessage id="Not informed" />
        </div>
      )
  }
}

function PendingEdits() {
  const currentProjectId = useSelector(state => state.user?.selectedProject?.id)
  const allProjectIds = useSelector(state =>
    state.user?.profile?.projects?.map(item => item.id)
  )
  const [allLocations, setAllLocations] = React.useState(
    JSON.parse(localStorage.getItem('state'))?.allLocationsToogle !== undefined
      ? JSON.parse(localStorage.getItem('state'))?.allLocationsToogle
      : true
  )
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false)
  const [googleUpdateFilter, setGoogleUpdateFilter] = React.useState([])
  const [googleUpdatePending, setGoogleUpdatePending] = React.useState([])
  const locale =
    JSON.parse(localStorage.getItem('locasty_default_lang'))?.locale || 'en'
  const userProfile = useSelector(state => state.user?.profile)
  const userIsClient = userProfile?.role === CLIENT
  const { query } = useSearchParamsHandler()
  const [{ data, loading }] = useAxios({
    url: `/locations/pending`,
    method: 'GET',
    params: {
      project: allLocations ? undefined : currentProjectId,
      ...query,
    },
  })

  function handleToggleAllLocations(bool) {
    setAllLocations(bool)
    const previousState = localStorage.getItem('state')
      ? JSON.parse(localStorage.getItem('state'))
      : {}
    localStorage.setItem(
      'state',
      JSON.stringify({
        ...previousState,
        allLocationsToogle: bool,
      })
    )
  }

  const HeaderMenu = React.useCallback(() => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <Show condition={allProjectIds.length > 1}>
          <StyledSwitch
            id="alllocations-switch"
            checked={allLocations}
            style={{ zIndex: '0 !important' }}
            onChange={e => handleToggleAllLocations(e.target.checked)}
            type="switch"
            inline
          >
            <span>
              <FormText className="text-bold-500 d-inline-block">
                <FormattedMessage id="View all account locations" />
              </FormText>
            </span>
          </StyledSwitch>
        </Show>
      </div>
    )
  }, [allLocations, allProjectIds])

  const PENDING_COLUMNS = [
    {
      id: 'code',
      title: 'Code',
      cell: item => (
        <SimpleTextColumn>{item?.data?.storeCode}</SimpleTextColumn>
      ),
      sortable: true,
    },
    {
      id: 'category',
      title: 'Category',
      cell: item => (
        <SimpleTextColumn>
          {item?.data?.categories?.primaryCategory?.displayName}
        </SimpleTextColumn>
      ),
      sortable: true,
    },
    {
      id: 'status',
      title: 'Status',
      centered: true,
      width: '40%',
      cell: item => (
        <StatusLabelDiv id={`status-${item.id}`}>
          <div>
            <svg
              width="19"
              height="20"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Icon / Filled">
                <path
                  id="Vector"
                  d="M12 22.8008C6.477 22.8008 2 18.3238 2 12.8008C2 7.27778 6.477 2.80078 12 2.80078C17.523 2.80078 22 7.27778 22 12.8008C22 18.3238 17.523 22.8008 12 22.8008ZM11 15.8008V17.8008H13V15.8008H11ZM11 7.80078V13.8008H13V7.80078H11Z"
                  fill="#DD6B20"
                />
              </g>
            </svg>
          </div>
          <div>
            {
              item.googleUpdates.filter(
                item => !googleUpdateFilter.includes(item.id)
              ).length
            }{' '}
            <FormattedMessage
              id={
                item.googleUpdates.filter(
                  item => !googleUpdateFilter.includes(item.id)
                ).length === 1
                  ? 'Pending Edit'
                  : 'Pending Edits'
              }
            />
          </div>
        </StatusLabelDiv>
      ),
    },
    {
      id: 'state',
      title: 'State',
      cell: item => (
        <SimpleTextColumn>
          {item?.data?.storefrontAddress?.administrativeArea}
        </SimpleTextColumn>
      ),
      sortable: true,
    },
    {
      id: 'city',
      title: 'City',
      cell: item => (
        <SimpleTextColumn>
          {item?.data?.storefrontAddress?.locality}
        </SimpleTextColumn>
      ),
      sortable: true,
    },
    {
      id: 'action',
      title: 'Action',
      centered: true,
      cell: (item, isExtended, extend) => (
        <PendingAction
          href={`/${item.id}/edit`}
          onClick={extend}
          disabled={
            !item.googleUpdates.filter(
              item => !googleUpdateFilter.includes(item.id)
            ).length
          }
        >
          {isExtended ? (
            <ChevronDown
              size="20"
              color={
                item.googleUpdates.filter(
                  item => !googleUpdateFilter.includes(item.id)
                ).length
                  ? '#606060'
                  : '#d7d7d7'
              }
            />
          ) : (
            <ChevronRight
              size="20"
              color={
                item.googleUpdates.filter(
                  item => !googleUpdateFilter.includes(item.id)
                ).length
                  ? '#606060'
                  : '#d7d7d7'
              }
            />
          )}
        </PendingAction>
      ),
    },
  ]

  const PendingReviewsExpanded = (data, locale, extend) => {
    return (
      <>
        <PendingExpandedDiv>
          <div className="pending-row pending-header">
            <div>
              <FormattedMessage id="From" />:
            </div>
            <div>
              <FormattedMessage id="To" />:
            </div>
          </div>
          {data?.googleUpdates
            ?.filter(item => !googleUpdateFilter.includes(item.id))
            ?.map((item, index, arr) => {
              return (
                <div className="pending-row" key={index}>
                  <div className="pending-from">
                    <div>
                      <FormattedMessage
                        id={MASK_TO_LABEL[item.diffMask] ?? ''}
                      />
                    </div>
                    <div>
                      {renderDiffObject(
                        _.pick(data?.data, [item.diffMask]),
                        item.diffMask,
                        locale
                      )}
                    </div>
                  </div>
                  <div className="pending-to">
                    <div>
                      <div className="circle-chevron">
                        <ChevronRight size="14" color="#2D3748" />
                      </div>
                      <FormattedMessage
                        id={MASK_TO_LABEL[item.diffMask] ?? ''}
                      />
                    </div>
                    <div>
                      {renderDiffObject(item.diffObject, item.diffMask, locale)}
                    </div>
                    {!userIsClient && (
                      <div className="pending-buttons">
                        <button
                          className="pending-reject"
                          disabled={isButtonDisabled}
                          onClick={async () => {
                            setIsButtonDisabled(true)
                            await handleRejectGoogleUpdate(
                              item.id,
                              arr.length,
                              extend,
                              data.id
                            )
                            setIsButtonDisabled(false)
                          }}
                        >
                          <FormattedMessage id="Reject" />
                        </button>
                        <button
                          className="pending-accept"
                          disabled={isButtonDisabled}
                          onClick={async () => {
                            setIsButtonDisabled(true)
                            await handleAcceptGoogleUpdate(
                              item.id,
                              arr.length,
                              extend,
                              data.id
                            )
                            setIsButtonDisabled(false)
                          }}
                        >
                          <FormattedMessage id="Approve" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
        </PendingExpandedDiv>
      </>
    )
  }

  async function handleAcceptGoogleUpdate(id, length, close, locationId) {
    await api
      .post(`/google-updates/${id}/accept`)
      .then(() => {
        setGoogleUpdateFilter(prevState => [...prevState, id])
        if (length === 1) {
          setGoogleUpdatePending(prevState => [...prevState, locationId])
          close()
        }
      })
      .catch(res => {
        console.error(res)
        toast.error('Something went wrong while accepting the update')
      })
  }

  async function handleRejectGoogleUpdate(id, length, close, locationId) {
    await api
      .post(`/google-updates/${id}/reject`)
      .then(() => {
        setGoogleUpdateFilter(prevState => [...prevState, id])
        if (length === 1) {
          setGoogleUpdatePending(prevState => [...prevState, locationId])
          close()
        }
      })
      .catch(res => {
        console.error(res)
        toast.error('Something went wrong while rejecting the update')
      })
  }

  return (
    <PendingEditsDiv>
      <BreadcrumbHeader path={[{ name: 'Pending Edits', path: 'pending' }]}>
        <HeaderMenu />
      </BreadcrumbHeader>

      <CustomTable
        hasPagination
        hasFilter
        header="Pending Edits"
        columns={PENDING_COLUMNS}
        data={data?.pending?.filter(
          item => !googleUpdatePending.includes(item.id)
        )}
        total={data?.total}
        loading={loading}
        skeletonHeight={'30rem'}
        extendableComponent={(item, _, extend) =>
          PendingReviewsExpanded(item, locale, extend)
        }
        noDataComponent={
          <NoData>
            <ClipboardIcon width="150" />
            <div>
              <FormattedMessage id="No Pending Edits" />
            </div>
          </NoData>
        }
      />
    </PendingEditsDiv>
  )
}

const PendingEditsDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const SimpleTextColumn = styled.span`
  color: #161616;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1rem;
`

const PendingAction = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`

const StatusLabelDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: #fffaf0;
  color: #dd6b20;
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  gap: 0.5rem;
`

const PendingExpandedDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 0.7rem;
  background-color: #f9f9f9;

  .pending-header {
    color: #5e5873;
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: Montserrat;
    font-size: 0.9rem;
    font-style: normal;
    font-weight: 700;

    > div:nth-child(1) {
      width: 40%;
    }

    > div:nth-child(2) {
      width: 60%;
    }
  }

  .pending-row {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    > div {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 0.5rem;

      > div:nth-child(1) {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 0.5rem;
        color: #161616;
        font-family: Montserrat;
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        min-width: 200px;
        max-width: 200px;
      }

      > div:nth-child(2) {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        color: #161616;
        font-family: Montserrat;
        font-size: 1rem;
        font-style: normal;
        font-weight: 700;
        overflow-wrap: anywhere;
        max-width: 250px;
      }
    }

    .pending-from {
      width: 40%;
    }

    .pending-to {
      width: 60%;
    }
  }

  .circle-chevron {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 15px;
    height: 15px;
    border-radius: 9999999px;
    background-color: #fff;
    aspect-ratio: 1/1;
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
      0px 1px 2px 0px rgba(0, 0, 0, 0.06);
  }

  .pending-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
    align-self: center;
    gap: 1em;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.5em 1em;
      border: none;
      border-radius: 0.5rem;
      font-family: Montserrat;
      font-size: 1rem;
      font-style: normal;
      font-weight: 500;

      &:disabled {
        cursor: not-allowed;
      }
    }

    .pending-reject {
      background-color: transparent;
      color: #ed5f5f;

      &:disabled {
        color: #ed8080;
      }
    }

    .pending-accept {
      background-color: #6bb156;
      color: #fff;

      &:disabled {
        color: #8dbf7e;
      }
    }
  }
`

const RegularHoursDiv = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.2rem;
  }
`

const NoData = styled.div`
  height: 30rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  gap: 2rem;
  color: #767676;
  font-size: 1.5rem;
  font-weight: 600;
`

const PendingEditsWrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )
  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 10),
        sort: withDefault(StringParam, ''),
        sortBy: withDefault(StringParam, ''),
        search: StringParam,
      }}
    >
      <PendingEdits />
    </SearchParamsHandlerProvider>
  )
}

export default PendingEditsWrapper
